import { ApiError } from "@hakili/api-client";
import { t } from "@hakili/i18n";
import { Callout, Field, GuardedButton, Input } from "@hakili/ui";
import { CircleAlert, Info, TriangleAlert } from "lucide-react";
import { useId, useState } from "react";

import { GUARD_CHIP } from "./d2-parts.js";
import {
  InertPrimary,
  LOGIN_ACT,
  LOGIN_FORM,
  LOGIN_SUB,
  LOGIN_TITLE,
  LoginShell,
  QuietLink,
} from "./connexion-panel.js";

/**
 * `RESET-PASSWORD`, R2 (D-1024-4, D-1024-10; frames 101, 102, 103 and 109 of `design/bank-v2` at `10c95f31`): **« Mot de
 * passe oublié », the request.**
 *
 * One field, one button. The door answers `202` for ANY address (bank staff or not, known or not), so « Vérifiez votre boîte
 * mail » says « Si un compte correspond à », never « un e-mail est parti »: it must not tell a banker from a stranger. The
 * throttle is the door's too (`429`, three an hour per SUBMITTED address, existing or not), and the screen says what the door
 * says: the frame's one sentence, in a box above the field, with « Envoyer le lien » inert and carrying NO sentence of its own
 * (the reason is said once, in the box: the button's description points at it).
 *
 * **The words of a refusal that is not that one are the door's own** (the source's ceiling, a door that is down), as the
 * invitation's screen does it: this screen draws one refusal, and invents no sentence for another.
 */

/** The only refusal frame 109 draws: the door's own `429` for an ADDRESS. */
const ADDRESS_LOCKED_WORDS = `${t("bank.oubli.tropDemandes")} ${t("bank.oubli.reessayerHeure")}`;

const looksLikeAddress = (value: string): boolean => /\S+@\S+\.\S+/u.test(value);

type Stage =
  | { readonly kind: "ask" }
  | { readonly kind: "sent"; readonly email: string }
  | { readonly kind: "locked"; readonly words: string }
  | { readonly kind: "failed"; readonly words: string };

export function MotDePasseOublie(props: {
  readonly request: (email: string) => Promise<void>;
  readonly onBack: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<Stage>({ kind: "ask" });
  const [pending, setPending] = useState(false);
  const boxId = useId();
  const address = email.trim();
  const ready = looksLikeAddress(address);

  const submit = (event: Pick<Event, "preventDefault">): void => {
    event.preventDefault();
    if (!ready || pending || stage.kind === "locked") return;
    setPending(true);
    props
      .request(address)
      .then(() => {
        setStage({ kind: "sent", email: address });
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.status === 429) {
          setStage({ kind: "locked", words: cause.message });
          return;
        }
        setStage({
          kind: "failed",
          words:
            cause instanceof ApiError && cause.message !== "" ? cause.message : t("common.error.inattendue"),
        });
      })
      .finally(() => {
        setPending(false);
      });
  };

  if (stage.kind === "sent") {
    return (
      <LoginShell>
        <h1 className={LOGIN_TITLE} data-testid="bank-oubli-verifiez-titre">
          {t("bank.oubli.verifiezTitre")}
        </h1>
        <p className={LOGIN_SUB} data-testid="bank-oubli-verifiez">
          {t("bank.oubli.verifiezAvant")}
          <strong>{stage.email}</strong>
          {t("bank.oubli.verifiezApres")}
        </p>
        <div className={LOGIN_FORM}>
          <Callout tone="info" icon={<Info className="h-4 w-4" aria-hidden="true" />}>
            <span data-testid="bank-oubli-valable">{t("bank.oubli.lienValable")}</span>
          </Callout>
          <QuietLink onClick={props.onBack} testId="bank-oubli-retour">
            {t("bank.oubli.retour")}
          </QuietLink>
          <QuietLink
            onClick={() => {
              setStage({ kind: "ask" });
            }}
            testId="bank-oubli-nouveau-lien"
          >
            {t("bank.oubli.nouveauLien")}
          </QuietLink>
        </div>
      </LoginShell>
    );
  }

  const locked = stage.kind === "locked";
  return (
    <LoginShell>
      <h1 className={LOGIN_TITLE} data-testid="bank-oubli-titre">
        {t("bank.oubli.titre")}
      </h1>
      <p className={LOGIN_SUB} data-testid="bank-oubli-consigne">
        {t("bank.oubli.consigne")}
      </p>
      <form
        className={LOGIN_FORM}
        onSubmit={submit}
        data-testid="bank-oubli"
        aria-label={t("bank.oubli.titre")}
        noValidate
      >
        {locked ? (
          <div id={boxId} data-testid="bank-oubli-trop">
            <Callout tone="warn" icon={<TriangleAlert className="h-4 w-4" aria-hidden="true" />}>
              {stage.words === ADDRESS_LOCKED_WORDS || stage.words.trim() === "" ? (
                <>
                  <strong>{t("bank.oubli.tropDemandes")}</strong> {t("bank.oubli.reessayerHeure")}
                </>
              ) : (
                stage.words
              )}
            </Callout>
          </div>
        ) : null}
        {stage.kind === "failed" ? (
          <Callout
            tone="bad"
            icon={<CircleAlert className="h-4 w-4" aria-hidden="true" />}
            testId="bank-oubli-erreur"
          >
            {stage.words}
          </Callout>
        ) : null}
        <Field label={t("bank.login.email")} htmlFor="bank-oubli-email">
          <Input
            id="bank-oubli-email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="username"
            required
            testId="bank-oubli-email"
          />
        </Field>
        <div className={LOGIN_ACT}>
          {locked ? (
            <InertPrimary describedBy={boxId} testId="bank-oubli-envoyer">
              {t("bank.oubli.envoyer")}
            </InertPrimary>
          ) : ready ? (
            <GuardedButton type="submit" variant="primary" size="lg" testId="bank-oubli-envoyer">
              {t("bank.oubli.envoyer")}
            </GuardedButton>
          ) : (
            <GuardedButton
              disabled
              type="submit"
              size="lg"
              reason={t("bank.oubli.saisirAdresse")}
              {...GUARD_CHIP}
              testId="bank-oubli-envoyer"
            >
              {t("bank.oubli.envoyer")}
            </GuardedButton>
          )}
        </div>
        <QuietLink onClick={props.onBack} testId="bank-oubli-retour">
          {t("bank.oubli.retour")}
        </QuietLink>
      </form>
    </LoginShell>
  );
}
